import './TableModifier.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { FilterComponent } from './filters/FilterComponent';
import { FilterDisplay } from './FilterDisplay';
import { FilterMenu } from './FilterMenu';
import { objectKeys } from '@sonar-software/sonar-ts-lib';
import { PaginatedTableQueryParams } from '@ui/tables';
import { SchemaFormat } from '@sonar-software/sonar-core';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { SortedBySelect } from './SortedBy';
import { TableOutlined } from '@ant-design/icons';
import { translateFilterAndOrderingToQueryParams } from './@arch/lib';
import { When } from 'react-if';

import { Button, Row } from 'antd';
import { ColumnInfo, ColumnsModal, ColumnsQueryParams } from './ColumnsModal';
import { DynamicFilter, DynamicQueryParams, SonarObjectType, SortByOption } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UpdateQueryParams, useBoolean } from '@hooks';

export type TableFilter = {
	filterComponent?: FilterComponent;
	filterFormat: SchemaFormat;
	filterGroup?: string;
	filterId: string;
	filterLabel: string;
	filterMetadata?: string;
};

export type TableQueryParams = DynamicQueryParams &
	PaginatedTableQueryParams &
	SortByFilterQueryParams &
	ColumnsQueryParams;

export type TableFilterQueryParams = DynamicFilter & SortByFilterQueryParams & {
		filterCount?: number;
		sortDefault?: SortByOption
	};

export type ConvertToDynamicQueryLanguage = (queryParams: TableFilterQueryParams | TableQueryParams) =>
	Partial<TableQueryParams>;

export type ConvertToQueryParams = (
	filters: Array<TableFilter>,
	sortOptions: Array<SortByOption>,
	filterString?: string,
	orderingString?: string,
    displayNameString?: string
) => Partial<TableFilterQueryParams>;

export interface TableModifierProps {
	allowColumnConfig?: boolean;
	className?: string;
	actionSection?: React.ReactNode;
	convertToDynamicQueryLanguage?: ConvertToDynamicQueryLanguage;
	convertToQueryParams?: ConvertToQueryParams;
	columns?: ColumnInfo[];
	columnsString?: string;
	customArrangers?: React.ReactNode;
	deferFiltering?: boolean;
	extra?: React.ReactNode;
	filters: Array<TableFilter>;
	filtersString?: string;
	filterDisplayType: 'inline' | 'menu';
	onChange: UpdateQueryParams<TableQueryParams>;
	orderString?: string;
	sonarObjectId?: number | null;
	sonarObjectType?: SonarObjectType | null;
	sortOptions: Array<SortByOption>;
    displayNameString?: string;
}

export const TableModifier: React.FC<TableModifierProps> = ({
	allowColumnConfig,
	className,
	actionSection,
	convertToDynamicQueryLanguage,
	convertToQueryParams = translateFilterAndOrderingToQueryParams,
	columns = [],
	columnsString,
	customArrangers,
	deferFiltering,
	extra,
	filters,
	filtersString,
	filterDisplayType,
	onChange,
	orderString,
	sonarObjectId,
	sonarObjectType,
	sortOptions,
	displayNameString
}) => {
	const [ showColumnsModal, handleSetColumnsModal ] = useBoolean(false);
	const [ filterParams, setFilterParams ] = useState(
		() => convertToQueryParams(filters, sortOptions, filtersString, orderString, displayNameString)
	);

	const onChangeRef = useRef(onChange);

	onChangeRef.current = onChange;

	useEffect(() => {
		const newFilterParams = convertToQueryParams(
			filters,
			sortOptions,
			filtersString,
			orderString,
			displayNameString
		);

		setFilterParams(newFilterParams);
	}, [ convertToQueryParams, displayNameString, filters, filtersString, orderString, sortOptions ]);

	const handleUpdate = useCallback(
		(update: Partial<TableFilterQueryParams>) => {
			let newParams: TableFilterQueryParams;

			if (objectKeys(update).length) {
				newParams = {
					...filterParams,
					...update
				} as TableFilterQueryParams;
			} else {
				newParams = {
					...filterParams,
					filterCount: 0
				} as TableFilterQueryParams;

				filters.forEach((filter) => {
					newParams[ filter.filterId ].value =
						Array.isArray(newParams[ filter.filterId ].value) ? [] : undefined;
				});
			}

			setFilterParams(newParams);

			const dql = convertToDynamicQueryLanguage?.(newParams);

			if (dql) {
				onChangeRef.current(filterParams.sortBy || newParams.sortBy ? dql : {
					displays: dql.displays,
					filters: dql.filters
				});
			}
		},
		[ convertToDynamicQueryLanguage, filterParams, filters ]
	);

	const showFilterMenu = filterDisplayType === 'menu';

	return (
		<>
			<Row
				className={ classNames('table-modifier', { 'with-compare': Boolean(actionSection),
					'with-menu': showFilterMenu
					 }, className) }>
				<div className='filters-wrapper'>
					<div className='compare-button-wrapper'>
						<div className='compare-button-container'>
							{ actionSection }
						</div>
					</div>
					<div className='filters'>
						{ filters?.map((filter) =>
							<FilterDisplay
								displayAsTag={ showFilterMenu }
								filter={ filter }
								filterParams={ filterParams as DynamicFilter }
								key={ filter.filterId }
								sonarObjectId={ sonarObjectId }
								sonarObjectType={ sonarObjectType }
								updateFilterParams={ handleUpdate } />) }
						<When condition={ showFilterMenu && Boolean(filterParams.filterCount) }>
							<Button
								onClick={ () => handleUpdate({}) }
								size='small'
								type='link'>
								{ COPY_SPECIFIC.CLEAR_ALL }
							</Button>
						</When>
					</div>
				</div>
				<div className='arrangers'>
					{ customArrangers }
					{ allowColumnConfig &&
						<Button
							icon={ <TableOutlined /> }
							onClick={ handleSetColumnsModal.on }
							type='text'>
							{ COPY_SPECIFIC.COLUMNS }
						</Button> }
					<When condition={ showFilterMenu }>
						<FilterMenu
							deferFiltering={ deferFiltering }
							filterParams={ filterParams as DynamicFilter }
							filters={ filters }
							sonarObjectId={ sonarObjectId }
							sonarObjectType={ sonarObjectType }
							updateFilterParams={ handleUpdate } />
					</When>
					<SortedBySelect
						defaultValue={ filterParams.sortDefault || sortOptions[ 0 ] }
						onChange={ handleUpdate }
						queryParams={ filterParams }
						sortOptions={ sortOptions } />
					{ extra }
				</div>
			</Row>
			<When condition={ allowColumnConfig }>
				<ColumnsModal
					columns={ columns }
					columnsString={ columnsString }
					onCancel={ handleSetColumnsModal.off }
					onChange={ onChange }
					open={ showColumnsModal } />
			</When>
		</>
	);
};
